/**
 * MenuDialog — full-screen table of contents.
 * Real dialog semantics: focus trap, Escape to close, focus restore,
 * body scroll lock and an inert backdrop.
 *
 * Motion: the dialog has a real enter and exit presence (fade + short panel
 * translation) with a staggered reveal of the chapter rows. Closing is
 * immediate in terms of responsiveness — navigation and focus restore never
 * wait for the exit animation, the node is only unmounted afterwards.
 * prefers-reduced-motion collapses every duration to zero.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CHAPTERS } from "@/components/nav/chapters";
import { useAnchorNav, prefersReducedMotion } from "@/utils/anchorNav";
import { cn } from "@/utils/cn";

interface MenuDialogProps {
  open: boolean;
  onClose: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const EXIT_MS = 240;

const MenuDialog = ({ open, onClose }: MenuDialogProps) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const handleAnchor = useAnchorNav();

  /** Kept mounted through the exit animation. */
  const [mounted, setMounted] = useState(open);
  /** Drives the enter/exit CSS state. */
  const [shown, setShown] = useState(false);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // Mount / unmount lifecycle, race-safe on rapid close→reopen.
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two frames: the initial (hidden) state must be painted once,
      // otherwise the browser skips the enter transition entirely.
      let inner = 0;
      const raf = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(raf);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    if (!mounted) return;
    const delay = prefersReducedMotion() ? 0 : EXIT_MS;
    const timer = window.setTimeout(() => setMounted(false), delay);
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  // Scroll lock + focus management tied to the *logical* open state,
  // so closing restores focus and scrolling immediately.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }, 20);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(timer);
      restoreRef.current?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      className={cn("fixed inset-0 z-[70]", shown && open ? "menu-open" : !open && "pointer-events-none")}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        aria-label={t("nav.closeMenu")}
        tabIndex={-1}
        onClick={onClose}
        className="menu-backdrop absolute inset-0 h-full w-full cursor-default bg-ink-deep/80 backdrop-blur-sm"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("nav.contents")}
        className="menu-panel absolute inset-0 flex flex-col overflow-y-auto bg-ink text-white"
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between gap-4 py-5">
            <Link to="/" onClick={onClose} aria-label={t("nav.homeAria")} className="inline-flex items-center transition-opacity duration-200 hover:opacity-80">
              <img
                src="/assets/oceannova-horizontal-logo.png"
                alt="OceanNova"
                className="h-auto w-[150px] [filter:brightness(0)_invert(1)] md:w-[190px]"
                width="1701"
                height="501"
              />
            </Link>

            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="light" />
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 border border-white/35 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t("nav.close")}
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <nav
          className="container mx-auto flex-1 px-6 pb-14 pt-6 md:pt-10"
          aria-label={t("nav.contents")}
        >
          <p className="eyebrow mb-6 text-white/55">{t("nav.contents")}</p>
          <ul className="border-t border-white/15">
            {CHAPTERS.map((c, i) => {
              const content = (
                <>
                  <span className="index-num text-base text-cobalt-bright md:text-lg">{c.index}</span>
                  <span className="flex-1 text-2xl font-extrabold tracking-tight md:text-4xl">
                    {t(`nav.${c.key}`)}
                  </span>
                  <ArrowRight
                    className="h-5 w-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1.5 group-focus-visible:translate-x-1.5"
                    aria-hidden="true"
                  />
                </>
              );
              const cls =
                "group flex w-full items-center gap-5 border-b border-white/15 py-5 text-left text-white transition-colors duration-200 hover:text-cobalt-bright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:py-7";
              return (
                <li
                  key={c.key}
                  className="menu-row"
                  style={{ ["--row-delay" as string]: `${80 + i * 55}ms` }}
                >
                  {c.hash ? (
                    <a
                      href={`/#${c.hash}`}
                      className={cls}
                      onClick={(e) => {
                        onClose();
                        handleAnchor(c.hash as string)(e);
                      }}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link to={c.to as string} className={cls} onClick={onClose}>
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          <Link
            to="/privacy"
            onClick={onClose}
            className="menu-row mt-8 inline-block text-sm uppercase tracking-[0.14em] text-white/60 transition-colors duration-200 hover:text-white"
            style={{ ["--row-delay" as string]: `${80 + CHAPTERS.length * 55}ms` }}
          >
            {t("footer.privacy")}
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default MenuDialog;
